import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  AppWrapper,
  Card,
  CarouselView,
  DrawerItem,
  GridView,
  MarketplaceDetailsSkeleton,
  MarketplaceExploreCard,
  Subtitle,
  Title,
  VendorCheckNotice,
  ViewDetailsSidebar,
} from '../../components/_mui';
import {
  BACK_TO_TOP_LABEL,
  CHOOSE_OPTIONS_LABEL,
  CHOOSE_YOUR_KIT_TITLE,
  CONTINUE_TO_OPTIONS_LABEL,
  CONTINUE_TO_SHIPPING_LABEL,
  DETAILS_FAILED_TOAST,
  ENTRY_BANNER_MIN_KITS,
  getChooseKitsSubtitleText,
  getChooseKitSubtitleText,
  getKitSubtitleText,
  MARKETPLACE_TOTAL_CREDITS,
  PICKED_FOR_YOU_TITLE,
  TAKE_KIT_OR_PICK_HINT,
  TAKING_THE_KIT_TEXT,
} from '../../constants/marketplace';
import { ROUTE_NAMES } from '../../constants/routes';
import {
  PYG_DOWN_HIDE_AT,
  PYG_UP_THRESHOLDS,
  SCROLL_LANDING_OFFSET_MOBILE,
  SELECTION_CONFIRM_MS,
} from '../../constants/ui';
import { useFab } from '../../hooks/useFab';
import { useFooter } from '../../hooks/useFooter';
import { useLogoScale } from '../../hooks/useLogoScale';
import useWindowSize from '../../hooks/useWindowSize';
import { setPickedCampaignId, submitKitChoice, submitPickYourGiftStep } from '../../store/actions/form';
import { marketplaceHydrateItemsRequestAction } from '../../store/actions/marketplace';
import { selectIsKitChoiceSend, selectPickedCampaign } from '../../store/selectors/derived';
import { selectPickedCampaignId } from '../../store/selectors/form';
import { selectCampaigns, selectIsEmbed, selectItems, selectSenderCompanyName } from '../../store/selectors/init';
import {
  selectCatalogCampaigns,
  selectHasGiftBudget,
  selectIsMarketplaceCampaignLoadingFn,
  selectIsMarketplaceCampaignSettledFn,
  selectMarketplaceCampaignProductIds,
  selectMarketplaceItemImagesFn,
  selectMarketplaceItemOptionRowsFn,
  selectMarketplaceItemStatusFn,
  selectMarketplaceItemVendorFn,
  selectNeedsMarketplaceOptionsStepFn,
} from '../../store/selectors/marketplace';
import { MarketplaceDetailsStatusEnum } from '../../types/marketplace';
import { ICampaign, ICampaignItem } from '../../types/pyg';
import { DescriptionSourceEnum, IBrandingLocationState } from '../../types/shell';
import { isMskuIncluded, isMskuItem } from '../../util/helpers';
import {
  addMarketplacePrefix,
  collectMarketplaceProductIds,
  getCampaignMarketplaceProductIds,
  isDetailsSettled,
  isMarketplaceId,
} from '../../util/marketplace';
import { MarketplaceEntryBanner } from '../Marketplace/components/MarketplaceEntryBanner';
import { CampaignCard } from './components/CampaignCard';

const getCampaignKey = (campaign: ICampaign) => campaign.id;
const getCampaignItemKey = (item: ICampaignItem) => item.id;

const KIT_DRAWER_ID = 'kit-drawer';
type TDrawerCampaign = Pick<ICampaign, 'id' | 'name' | 'items'>;

const selectionSectionSx: SxProps<Theme> = { display: 'flex', flex: 1, flexDirection: 'column' };
const carouselWrapperSx: SxProps<Theme> = { display: 'flex', flexDirection: 'column', marginTop: 3 };
const gridWrapperSx: SxProps<Theme> = { paddingBottom: 7 };
const continueIconSx: SxProps<Theme> = { fontSize: 18 };

const KIT_SLOTS = ['kit'];
const getSlotKey = (slot: string) => slot;

interface ISelectionLayoutProps<T> {
  items: T[];
  getItemKey: (item: T) => string | number;
  renderItem: (item: T) => React.ReactNode;
  isCarousel: boolean;
  footerItem?: React.ReactNode;
}

function SelectionLayout<T>({ items, getItemKey, renderItem, isCarousel, footerItem }: ISelectionLayoutProps<T>) {
  if (isCarousel) {
    return (
      <Box sx={carouselWrapperSx}>
        <CarouselView
          items={items}
          getItemKey={getItemKey}
          renderItem={renderItem}
          containerGutter={16}
          footerItem={footerItem}
        />
      </Box>
    );
  }

  return (
    <Box sx={gridWrapperSx}>
      <GridView items={items} getItemKey={getItemKey} renderItem={renderItem} footerItem={footerItem} />
    </Box>
  );
}

const PickYourGift = () => {
  const { mobile } = useWindowSize();
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'));
  const { headerHeight } = useLogoScale();
  const dispatch = useDispatch();
  const history = useHistory();

  const campaignList = useSelector(selectCampaigns);
  const inventoryItems = useSelector(selectItems);
  const selectedCampaignId = useSelector(selectPickedCampaignId);
  const selectedCampaignData = useSelector(selectPickedCampaign);
  const isEmbed = useSelector(selectIsEmbed);
  const hasGiftBudget = useSelector(selectHasGiftBudget);
  const isKitChoice = useSelector(selectIsKitChoiceSend);
  const catalogCampaigns = useSelector(selectCatalogCampaigns);
  const senderCompanyName = useSelector(selectSenderCompanyName);
  const itemVendorFn = useSelector(selectMarketplaceItemVendorFn);
  const itemOptionRowsFn = useSelector(selectMarketplaceItemOptionRowsFn);
  const itemImagesFn = useSelector(selectMarketplaceItemImagesFn);
  const needsOptionsStepOf = useSelector(selectNeedsMarketplaceOptionsStepFn);
  const isCampaignLoadingFn = useSelector(selectIsMarketplaceCampaignLoadingFn);
  const isCampaignSettledFn = useSelector(selectIsMarketplaceCampaignSettledFn);
  const itemStatusFn = useSelector(selectMarketplaceItemStatusFn);
  const selectMarketplaceIds = useMemo(
    () => selectMarketplaceCampaignProductIds(selectedCampaignId),
    [selectedCampaignId],
  );
  const selectedMarketplaceProductIds = useSelector(selectMarketplaceIds);

  const [open, setOpen] = useState(false);
  const [isKitSelected, setIsKitSelected] = useState(false);

  // One assembled kit plus a budget: the page is titled after the kit, with the marketplace as its
  // alternative. Counted over portal campaigns only, since a pre-selected marketplace campaign is
  // another pick beside the kit.
  const isKitSend = catalogCampaigns.length === 1 && hasGiftBudget && !isEmbed;

  const isKitAlternativeShown = isKitSend || isKitChoice;

  const kitCount = catalogCampaigns.length;
  const isKitsChoice = hasGiftBudget && kitCount >= 2 && kitCount < ENTRY_BANNER_MIN_KITS;

  const [currentCampaignId, setCurrentCampaignId] = useState<string | null>(null);
  const isKitDetails = isKitChoice && currentCampaignId === KIT_DRAWER_ID;
  const currentStoreCampaign = useMemo(
    () => campaignList?.find((campaign) => campaign.id === currentCampaignId),
    [campaignList, currentCampaignId],
  );
  const kitCampaign = useMemo<TDrawerCampaign | undefined>(
    () =>
      isKitChoice && inventoryItems
        ? { id: KIT_DRAWER_ID, name: PICKED_FOR_YOU_TITLE, items: inventoryItems }
        : undefined,
    [isKitChoice, inventoryItems],
  );
  const currentCampaign: TDrawerCampaign | undefined = isKitDetails ? kitCampaign : currentStoreCampaign;
  const [awaitedCampaignId, setAwaitedCampaignId] = useState<string | null>(null);
  const [focusSkuIndex, setFocusSkuIndex] = useState<number | undefined>(undefined);

  const selectionSectionRef = useRef<HTMLDivElement>(null);
  const confirmTimerRef = useRef<number>();

  const scrollToSelection = useCallback(
    (behavior: ScrollBehavior) => {
      const section = selectionSectionRef.current;
      if (!section) {
        return;
      }
      const top = section.getBoundingClientRect().top + window.scrollY;
      const offset = isMobileViewport ? SCROLL_LANDING_OFFSET_MOBILE : headerHeight;
      window.scrollTo({ top: Math.max(0, top - offset), behavior });
    },
    [isMobileViewport, headerHeight],
  );

  const scrollToTop = useCallback((behavior: ScrollBehavior) => window.scrollTo({ top: 0, behavior }), []);

  const fabConfig = useMemo(
    () => ({
      down: { label: 'Go to item selection', onActivate: scrollToSelection },
      up: { label: BACK_TO_TOP_LABEL, onActivate: scrollToTop },
      desktopFabEnabled: true,
      upThresholds: PYG_UP_THRESHOLDS,
      ...(isMobileViewport ? {} : { downHideAt: PYG_DOWN_HIDE_AT, downIgnoresBar: true }),
    }),
    [scrollToSelection, scrollToTop, isMobileViewport],
  );

  useFab(fabConfig);

  const fetchCampaignDetails = useCallback(
    (campaign: ICampaign): boolean => {
      const productIds = getCampaignMarketplaceProductIds(campaign);

      if (!productIds.length) {
        return false;
      }

      // Joining a running wave beats restarting it: takeLatest would cancel the first one and
      // strand its products on Loading.
      if (isCampaignLoadingFn(campaign.id)) {
        return true;
      }

      dispatch(marketplaceHydrateItemsRequestAction({ productIds }));

      return true;
    },
    [dispatch, isCampaignLoadingFn],
  );

  const handlePickCampaign = useCallback(
    (campaign: ICampaign) => {
      const newId = selectedCampaignId === campaign.id ? null : campaign.id;
      dispatch(setPickedCampaignId(newId));

      if (newId) {
        fetchCampaignDetails(campaign);
      }
    },
    [selectedCampaignId, dispatch, fetchCampaignDetails],
  );

  const handleBrowseMarketplace = useCallback(() => {
    history.push(ROUTE_NAMES.marketplace, { from: ROUTE_NAMES.pyg } as IBrandingLocationState);
  }, [history]);

  const kitMarketplaceProductIds = useMemo(() => collectMarketplaceProductIds(inventoryItems ?? []), [inventoryItems]);

  const isLoadingKitOptions = useMemo(
    () =>
      (inventoryItems ?? []).some(
        (item) => isMarketplaceId(item.id) && itemStatusFn(item.id) === MarketplaceDetailsStatusEnum.Loading,
      ),
    [inventoryItems, itemStatusFn],
  );

  const handleTakeKit = useCallback(() => {
    if (!isKitSelected && kitMarketplaceProductIds.length) {
      dispatch(marketplaceHydrateItemsRequestAction({ productIds: kitMarketplaceProductIds }));
    }

    setIsKitSelected((selected) => !selected);
  }, [isKitSelected, kitMarketplaceProductIds, dispatch]);

  const handleKitContinue = useCallback(() => {
    return new Promise<string>((resolve) => {
      dispatch(submitKitChoice({ resolve }));
    }).then(history.push);
  }, [dispatch, history]);

  const handleKitContinueClick = useCallback(() => {
    if (!isEmbed) {
      handleKitContinue();
    }
  }, [isEmbed, handleKitContinue]);

  const hasKitOptionsStep = useMemo(
    () => (inventoryItems ?? []).some((item) => isMskuItem(item) || needsOptionsStepOf(item.id)),
    [inventoryItems, needsOptionsStepOf],
  );

  const isKitSettled = useMemo(
    () =>
      (inventoryItems ?? [])
        .filter((item) => isMarketplaceId(item.id))
        .every((item) => isDetailsSettled(itemStatusFn(item.id))),
    [inventoryItems, itemStatusFn],
  );

  const handleViewKitDetails = useCallback(
    (skuIndex?: number) => {
      if (kitMarketplaceProductIds.length) {
        if (!isLoadingKitOptions) {
          dispatch(marketplaceHydrateItemsRequestAction({ productIds: kitMarketplaceProductIds }));
        }
        setAwaitedCampaignId(KIT_DRAWER_ID);
      } else {
        setAwaitedCampaignId(null);
      }
      setCurrentCampaignId(KIT_DRAWER_ID);
      setFocusSkuIndex(skuIndex);
      setOpen(true);
    },
    [kitMarketplaceProductIds, isLoadingKitOptions, dispatch],
  );

  // The sidebar needs the full details for options and the gallery, so an awaited campaign opens
  // from the effect below once its fetch lands.
  const handleViewDetails = useCallback(
    (campaign: ICampaign, skuIndex?: number) => {
      setCurrentCampaignId(campaign.id);
      setFocusSkuIndex(skuIndex);
      setAwaitedCampaignId(fetchCampaignDetails(campaign) ? campaign.id : null);
      setOpen(true);
    },
    [fetchCampaignDetails],
  );

  useEffect(() => {
    if (!awaitedCampaignId) {
      return;
    }
    const isSettled = awaitedCampaignId === KIT_DRAWER_ID ? isKitSettled : isCampaignSettledFn(awaitedCampaignId);

    if (!isSettled) {
      return;
    }
    // Settled covers a partial failure: the skeleton gives way to whatever loaded rather than
    // waiting on details that never arrive.
    setAwaitedCampaignId(null);
  }, [awaitedCampaignId, isCampaignSettledFn, isKitSettled]);

  const isAwaitingDetails = !!awaitedCampaignId;

  const awaitedVendor = useMemo(() => {
    if (!awaitedCampaignId) {
      return undefined;
    }
    if (awaitedCampaignId !== KIT_DRAWER_ID) {
      return itemVendorFn(awaitedCampaignId);
    }
    const [firstKitProductId] = kitMarketplaceProductIds;

    return firstKitProductId ? itemVendorFn(addMarketplacePrefix(firstKitProductId)) : undefined;
  }, [awaitedCampaignId, itemVendorFn, kitMarketplaceProductIds]);

  const handleDetailsClose = useCallback(() => {
    setOpen(false);
    setAwaitedCampaignId(null);
    setCurrentCampaignId(null);
    setFocusSkuIndex(undefined);
  }, []);

  const handleContinue = useCallback(
    (selectedId: string | null | undefined) => {
      return new Promise<string>((resolve) => {
        if (selectedId) {
          dispatch(
            submitPickYourGiftStep({
              campaignId: selectedId,
              resolve,
            }),
          );
        }
      }).then(history.push);
    },
    [history, dispatch],
  );

  const handleDetailsContinue = useCallback(() => {
    if (isKitDetails) {
      handleDetailsClose();

      if (!isKitSelected) {
        handleTakeKit();
      }

      confirmTimerRef.current = window.setTimeout(handleKitContinueClick, SELECTION_CONFIRM_MS);

      return;
    }

    if (!currentStoreCampaign) {
      return;
    }

    const campaignId = currentStoreCampaign.id;
    const isAlreadyPicked = selectedCampaignId === campaignId;

    handleDetailsClose();

    if (!isAlreadyPicked) {
      handlePickCampaign(currentStoreCampaign);
    }

    confirmTimerRef.current = window.setTimeout(() => {
      handleContinue(campaignId);
    }, SELECTION_CONFIRM_MS);
  }, [
    isKitDetails,
    isKitSelected,
    handleTakeKit,
    handleKitContinueClick,
    currentStoreCampaign,
    selectedCampaignId,
    handleDetailsClose,
    handlePickCampaign,
    handleContinue,
  ]);

  useEffect(() => () => window.clearTimeout(confirmTimerRef.current), []);

  const detailsButtonText = useMemo(() => {
    if (isKitDetails) {
      return hasKitOptionsStep ? CHOOSE_OPTIONS_LABEL : CONTINUE_TO_SHIPPING_LABEL;
    }
    if (!currentStoreCampaign) {
      return CONTINUE_TO_SHIPPING_LABEL;
    }
    const hasOptionsToPick = isMskuIncluded(currentStoreCampaign) || needsOptionsStepOf(currentStoreCampaign.id);

    return hasOptionsToPick ? CHOOSE_OPTIONS_LABEL : CONTINUE_TO_SHIPPING_LABEL;
  }, [isKitDetails, hasKitOptionsStep, currentStoreCampaign, needsOptionsStepOf]);

  const detailsFooterButton = useMemo(() => {
    return (
      <Button
        variant="contained"
        color="primary"
        onClick={handleDetailsContinue}
        endIcon={<ChevronRightIcon />}
        fullWidth={mobile}
      >
        {detailsButtonText}
      </Button>
    );
  }, [detailsButtonText, handleDetailsContinue, mobile]);

  const selectButtonSx = useMemo<SxProps<Theme>>(
    () =>
      isMobileViewport
        ? { height: 42, py: 0.75, px: 2.5, fontSize: 15, lineHeight: '26px', minWidth: 64 }
        : { height: 36, py: 0.75, px: 2, minWidth: 64 },
    [isMobileViewport],
  );

  const kitCard = useMemo(() => {
    if (!isKitChoice || !inventoryItems) {
      return null;
    }

    return (
      <Card
        title={PICKED_FOR_YOU_TITLE}
        items={inventoryItems}
        isSelected={isKitSelected && !isLoadingKitOptions}
        onSkuIndexClick={handleViewKitDetails}
        actions={
          isLoadingKitOptions ? (
            <VendorCheckNotice hasBorder fullWidth sx={selectButtonSx} />
          ) : (
            <Button
              fullWidth
              variant={isKitSelected ? 'contained' : 'outlined'}
              color="primary"
              size="medium"
              startIcon={isKitSelected ? <CheckCircleIcon /> : undefined}
              onClick={handleTakeKit}
              sx={selectButtonSx}
            >
              {isKitSelected ? 'Selected' : 'Select'}
            </Button>
          )
        }
      />
    );
  }, [
    isKitChoice,
    inventoryItems,
    isKitSelected,
    isLoadingKitOptions,
    handleTakeKit,
    handleViewKitDetails,
    selectButtonSx,
  ]);

  const renderCardItem = useCallback(
    (item: ICampaign) => {
      // A wait the sidebar is already showing its skeleton for is not repeated on the card.
      const isChecking = isCampaignLoadingFn(item.id) && awaitedCampaignId !== item.id;

      return (
        <CampaignCard
          campaign={item}
          isPicked={selectedCampaignData?.id === item.id}
          isChecking={isChecking}
          vendor={itemVendorFn(item.id)}
          buttonSx={selectButtonSx}
          onPick={handlePickCampaign}
          onViewDetails={handleViewDetails}
        />
      );
    },
    [
      selectedCampaignData?.id,
      handleViewDetails,
      handlePickCampaign,
      itemVendorFn,
      isCampaignLoadingFn,
      awaitedCampaignId,
      selectButtonSx,
    ],
  );

  const exploreCard = useMemo(() => {
    const hasCuratedOptions = isKitChoice || !!campaignList?.length;
    const isHidden = !hasGiftBudget || isEmbed || !hasCuratedOptions;

    if (isHidden) {
      return undefined;
    }

    return <MarketplaceExploreCard onBrowse={handleBrowseMarketplace} isKitAlternative={isKitAlternativeShown} />;
  }, [hasGiftBudget, isEmbed, isKitChoice, campaignList, isKitAlternativeShown, handleBrowseMarketplace]);

  const isSelectedMarketplaceCampaign = !!selectedMarketplaceProductIds.length;

  // Tracks the selected campaign's own products rather than the global hydration flag, which
  // would keep Continue disabled after this item already resolved.
  const isLoadingSelectedOptions = useMemo(
    () =>
      isSelectedMarketplaceCampaign &&
      selectedMarketplaceProductIds.some(
        (productId) => itemStatusFn(addMarketplacePrefix(productId)) === MarketplaceDetailsStatusEnum.Loading,
      ),
    [isSelectedMarketplaceCampaign, selectedMarketplaceProductIds, itemStatusFn],
  );

  const hasFailedSelectedOptions = useMemo(
    () =>
      isSelectedMarketplaceCampaign &&
      selectedMarketplaceProductIds.some(
        (productId) => itemStatusFn(addMarketplacePrefix(productId)) === MarketplaceDetailsStatusEnum.Failed,
      ),
    [isSelectedMarketplaceCampaign, selectedMarketplaceProductIds, itemStatusFn],
  );

  // The fetch is fire-and-forget, so a rejection reaches the page as a Failed status.
  useEffect(() => {
    if (hasFailedSelectedOptions) {
      toast.error(DETAILS_FAILED_TOAST);
    }
  }, [hasFailedSelectedOptions]);

  // Follows the fetched variant count: a single-variant item goes straight to shipping. While
  // the fetch is in flight the label stays neutral rather than naming a step it may take back.
  const continueButtonText = useMemo(() => {
    if (!selectedCampaignData || isLoadingSelectedOptions) {
      return 'Continue';
    }

    const hasOptionsStep = needsOptionsStepOf(selectedCampaignData.id) || isMskuIncluded(selectedCampaignData);

    return hasOptionsStep ? CONTINUE_TO_OPTIONS_LABEL : CONTINUE_TO_SHIPPING_LABEL;
  }, [selectedCampaignData, isLoadingSelectedOptions, needsOptionsStepOf]);

  // While the fetch is in flight the label stays neutral rather than naming a step it may take back.
  const kitContinueButtonText = useMemo(() => {
    if (!isKitSelected || isLoadingKitOptions) {
      return 'Continue';
    }

    return hasKitOptionsStep ? CONTINUE_TO_OPTIONS_LABEL : CONTINUE_TO_SHIPPING_LABEL;
  }, [isKitSelected, isLoadingKitOptions, hasKitOptionsStep]);

  const handleContinueClick = useCallback(() => {
    if (!isEmbed) {
      handleContinue(selectedCampaignData?.id);
    }
  }, [isEmbed, handleContinue, selectedCampaignData?.id]);

  const isContinueDisabled = !selectedCampaignData?.id || isLoadingSelectedOptions;

  const continueEndIcon = useMemo(
    () => (isMobileViewport ? undefined : <ChevronRightIcon sx={continueIconSx} />),
    [isMobileViewport],
  );

  const continueFooterButton = useMemo(() => {
    if (isKitChoice) {
      return (
        <Button
          variant="contained"
          color="primary"
          onClick={handleKitContinueClick}
          disabled={!isKitSelected || isLoadingKitOptions}
          endIcon={continueEndIcon}
          fullWidth={isMobileViewport}
          size="medium"
        >
          {kitContinueButtonText}
        </Button>
      );
    }

    return (
      <Button
        variant="contained"
        color="primary"
        onClick={handleContinueClick}
        disabled={isContinueDisabled}
        endIcon={continueEndIcon}
        fullWidth={isMobileViewport}
        size="medium"
      >
        {continueButtonText}
      </Button>
    );
  }, [
    isKitChoice,
    isKitSelected,
    isLoadingKitOptions,
    kitContinueButtonText,
    handleKitContinueClick,
    handleContinueClick,
    isContinueDisabled,
    continueEndIcon,
    isMobileViewport,
    continueButtonText,
  ]);

  // Conditionally rendered so the mobile bar collapses once something is selected.
  const footerHelperText = useMemo(() => {
    const variant = isMobileViewport ? 'body2' : 'body1';

    if (isKitChoice) {
      if (!isKitSelected || isLoadingKitOptions) {
        return (
          <Typography variant={variant} color="text.secondary">
            {TAKE_KIT_OR_PICK_HINT}
          </Typography>
        );
      }
      if (isMobileViewport) {
        return undefined;
      }

      return (
        <Typography variant={variant} color="primary">
          {TAKING_THE_KIT_TEXT}
        </Typography>
      );
    }

    if (selectedCampaignData) {
      return undefined;
    }

    return (
      <Typography variant={variant} color="text.secondary">
        Select 1 item to continue
      </Typography>
    );
  }, [isKitChoice, isKitSelected, isLoadingKitOptions, selectedCampaignData, isMobileViewport]);

  const footerConfig = useMemo(
    () => ({
      helperText: footerHelperText,
      submitButton: continueFooterButton,
    }),
    [footerHelperText, continueFooterButton],
  );

  useFooter(footerConfig, selectionSectionRef);

  const renderDrawerItem = useCallback(
    (item: ICampaignItem) => (
      <DrawerItem
        item={item}
        optionRows={itemOptionRowsFn(item.id)}
        images={itemImagesFn(item.id)}
        vendor={itemVendorFn(item.id)}
        descriptionSource={
          isMarketplaceId(item.id) ? DescriptionSourceEnum.Marketplace : DescriptionSourceEnum.Inventory
        }
        isMarketplace={isMarketplaceId(item.id)}
      />
    ),
    [itemOptionRowsFn, itemImagesFn, itemVendorFn],
  );

  const kitSubtitleSx = useMemo<SxProps<Theme>>(
    () => ({ display: 'block', mt: 1, mb: mobile ? 2 : 3, fontWeight: 'regular' }),
    [mobile],
  );

  const isEntryBannerShown = hasGiftBudget && !isEmbed && kitCount >= ENTRY_BANNER_MIN_KITS;

  const pageTitle = useMemo(() => {
    if (isKitChoice || isKitsChoice) {
      return CHOOSE_YOUR_KIT_TITLE;
    }

    return isKitSend ? 'Your kit' : 'Pick your item';
  }, [isKitChoice, isKitsChoice, isKitSend]);

  const kitSubtitle = useMemo(() => {
    if (isKitChoice) {
      return getChooseKitSubtitleText(MARKETPLACE_TOTAL_CREDITS, senderCompanyName);
    }
    if (isKitsChoice) {
      return getChooseKitsSubtitleText(MARKETPLACE_TOTAL_CREDITS, senderCompanyName);
    }

    return isKitSend ? getKitSubtitleText(MARKETPLACE_TOTAL_CREDITS, senderCompanyName) : undefined;
  }, [isKitChoice, isKitsChoice, isKitSend, senderCompanyName]);

  const isTitleFlush = mobile || !!kitSubtitle;

  const titleSx = useMemo<SxProps<Theme>>(() => (isTitleFlush ? { marginBottom: 0 } : {}), [isTitleFlush]);

  const gridItems = useMemo(() => campaignList ?? [], [campaignList]);

  const renderKitSlot = useCallback(() => kitCard, [kitCard]);

  const selectionView = useMemo(() => {
    if (isKitChoice) {
      return (
        <SelectionLayout
          items={KIT_SLOTS}
          getItemKey={getSlotKey}
          renderItem={renderKitSlot}
          isCarousel={isMobileViewport}
          footerItem={exploreCard}
        />
      );
    }

    return (
      <SelectionLayout
        items={gridItems}
        getItemKey={getCampaignKey}
        renderItem={renderCardItem}
        isCarousel={isMobileViewport}
        footerItem={exploreCard}
      />
    );
  }, [isKitChoice, renderKitSlot, isMobileViewport, gridItems, renderCardItem, exploreCard]);

  if (!campaignList && !isKitChoice) {
    return null;
  }

  return (
    <AppWrapper>
      <Title sx={titleSx}>{pageTitle}</Title>
      {kitSubtitle && (
        <Subtitle variant="body2" sx={kitSubtitleSx}>
          {kitSubtitle}
        </Subtitle>
      )}
      {isEntryBannerShown && <MarketplaceEntryBanner onBrowse={handleBrowseMarketplace} />}
      <Box ref={selectionSectionRef} sx={selectionSectionSx}>
        {selectionView}
      </Box>
      <ViewDetailsSidebar<ICampaignItem>
        open={open}
        onClose={handleDetailsClose}
        title={currentCampaign?.name}
        items={isAwaitingDetails ? undefined : currentCampaign?.items}
        renderItem={renderDrawerItem}
        getItemKey={getCampaignItemKey}
        headerContent={isAwaitingDetails ? <MarketplaceDetailsSkeleton vendor={awaitedVendor} /> : undefined}
        footerButton={
          isAwaitingDetails ? <VendorCheckNotice hasBorder hasChevron fullWidth={mobile} /> : detailsFooterButton
        }
        focusIndex={focusSkuIndex}
        hideItemsCount={isAwaitingDetails}
      />
    </AppWrapper>
  );
};

export default PickYourGift;
